import { useQuery } from '@apollo/client'
import { GET_ORDER_CHARGES } from '../graphql/queries'
import { OrderType } from '../types/order'

/**
 * What THIS order type is charged per order, answered by the API.
 *
 * The order type is a query VARIABLE, so switching Dine In → Takeaway re-asks
 * the API and the cart shows the number the API will actually charge. That is
 * the point of the argument: the rule for which types pay verpakkingskosten
 * lives in ONE place (the API's config/packaging.ts, which the order path also
 * uses) instead of being re-implemented here. A client-side copy of that rule
 * is free to drift, and when it drifts the ±2c price-integrity check rejects
 * the order at the till — the client is never the authority on price, so it
 * should not be the authority on who pays either.
 *
 * It also makes the fee OBSERVABLE: every switch is a request you can see in
 * the Network tab, which is what separates "the fee is switched off" from
 * "this client never asked". Without that, a POS showing no fee looked
 * identical to a POS that had never heard of one. (2026-08-27, after exactly
 * that took a while to diagnose.)
 *
 * Apollo caches per variable, so each type costs one request per session and
 * switching back is instant.
 */
export function useOrderCharges(orderType: OrderType) {
  const { data, loading } = useQuery(GET_ORDER_CHARGES, {
    variables: { order_type: orderType },
    fetchPolicy: 'cache-first',
    // The submitted total must match what the API computes, so a stale fee is
    // worse than one extra request per session.
    nextFetchPolicy: 'cache-and-network',
  })

  const packagingFeeCents: number = data?.publicOrderCharges?.packaging_fee_cents ?? 0

  /**
   * True when we have no answer yet — still loading, offline, or the query
   * errored. Zero is NOT a safe stand-in for it: the API resolves the fee
   * itself and ignores whatever the client sends, so a total missing €0,25
   * is not undercharged, it is REJECTED with "Order total has changed" after
   * the cashier has already quoted a price. Callers must gate checkout on
   * this rather than quietly proceed.
   *
   * No dine-in exemption here any more — the client no longer knows which
   * types are exempt, which is the whole point. That costs nothing in
   * practice: a client that cannot reach this query cannot submit an order
   * either, so blocking on it forfeits no sale that would have succeeded.
   */
  const packagingFeeUnknown = !data

  return { packagingFeeCents, packagingFeeUnknown, packagingFeeLoading: loading }
}
